import { useEffect, useState } from "react";
import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";
const linkedInPageHref = "https://www.linkedin.com/company/holocronsecurity";
const youtubeChannelHref = "https://www.youtube.com/@HolocronSecurity";

type LinkedInUpdate = {
  id: string;
  title: string;
  summary: string;
  publishedLabel: string;
  url: string;
  tags: string[];
};

type LinkedInFeed = {
  source: {
    platform: string;
    profileName: string;
    profileUrl: string;
    syncedAt: string | null;
    status: "seeded" | "synced" | "pending";
  };
  updates: LinkedInUpdate[];
};

const fallbackLinkedInFeed: LinkedInFeed = {
  source: {
    platform: "LinkedIn",
    profileName: "Holocron Security Inc.",
    profileUrl: linkedInPageHref,
    syncedAt: null,
    status: "pending"
  },
  updates: []
};

const publishedPlaceholders = [
  {
    type: "Article",
    title: "Published research placeholder",
    source: "External publication link coming soon"
  },
  {
    type: "Journal",
    title: "Journal contribution placeholder",
    source: "External publication link coming soon"
  },
  {
    type: "Brief",
    title: "Executive security brief placeholder",
    source: "External publication link coming soon"
  }
];

const topicCards = [
  {
    icon: "shield" as const,
    title: "Machine-speed cyber defense",
    copy:
      "Holocron's public perspective centers on the gap between machine-speed attacks and human-gated response models."
  },
  {
    icon: "brain" as const,
    title: "Secure AI implementation",
    copy:
      "Our work focuses on AI workflows that preserve policy boundaries, evidence handling, and operational review."
  },
  {
    icon: "document" as const,
    title: "CMMC Level 2 operations",
    copy:
      "We write and speak about making compliance evidence part of the operating environment instead of a late-stage scramble."
  }
];

const youtubeVideos = [
  {
    id: "8MufSMnCxF8",
    title: "Holocron Security Podcast | Episode 1: AI, Coding, and Technology",
    label: "Podcast",
    summary:
      "Craig Opie and Robert Delgado are joined by Blake Wallick for a conversation on AI, coding, and the evolving technology landscape."
  },
  {
    id: "luloWDO3uCY",
    title: "RapidFactory | Live Platform Demo Presented at the Holocron Security Mixer",
    label: "Platform demo",
    summary:
      "A live RapidFactory walkthrough showing compliant cloud infrastructure automation, secure Infrastructure as Code, and real-time posture visibility."
  },
  {
    id: "pVQqPzA7fjo",
    title: "RapidFactory | Secure EC2 Deployment with Continuous Compliance",
    label: "Platform demo",
    summary:
      "RapidFactory provisions an AWS EC2 instance from hardened templates with audit visibility and policy enforcement for regulated environments."
  },
  {
    id: "4fhvTrgpOPw",
    title: "RapidFactory | Real-Time Compliance Scanning & Infrastructure Control",
    label: "Platform demo",
    summary:
      "A demo focused on live compliance scans, infrastructure control, remediation, and centralized security posture visibility."
  },
  {
    id: "qxqtC0gu7rc",
    title: "RapidFactory | Secure EC2 Deployment in Minutes",
    label: "Platform demo",
    summary:
      "A rapid deployment walkthrough for secure AWS EC2 provisioning using compliance-ready Infrastructure as Code workflows."
  }
] as const;

const participationSnippets = [
  {
    title: "Kakoo Oiwi volunteer day",
    date: "June 2026",
    location: "Kaneohe, HI",
    source: "LinkedIn",
    sourceHref: linkedInPageHref,
    sourceIcon: "linkedin" as const,
    copy:
      "The Holocron team spent time restoring the loi with Kakoo Oiwi, connecting Conscious Security with service, team culture, and community impact.",
    tags: ["Community", "Team culture", "Hawaii"]
  },
  {
    title: "Holocron Security Podcast launch",
    date: "June 2026",
    location: "Na Koa Innovation Center",
    source: "LinkedIn + YouTube",
    sourceHref: `https://www.youtube.com/watch?v=${youtubeVideos[0].id}`,
    sourceIcon: "youtube" as const,
    copy:
      "Craig Opie and Robert Delgado opened the podcast with Blake Wallick for a discussion on AI, coding, and the technology landscape, recorded with studio support from Na Koa Innovation Center.",
    tags: ["Podcast", "AI", "Technology"]
  },
  {
    title: "Holocron Security Mixer",
    date: "December 10, 2025",
    location: "Honolulu, HI",
    source: "YouTube",
    sourceHref: "https://www.youtube.com/watch?v=luloWDO3uCY",
    sourceIcon: "youtube" as const,
    copy:
      "Holocron hosted customers, partners, and security leaders for a RapidFactory live platform demo focused on compliant cloud infrastructure and security-first Infrastructure as Code.",
    tags: ["RapidFactory", "Live demo", "Compliance"]
  },
  {
    title: "The Next Wave with PACE Hawaii",
    date: "2026",
    location: "University of Hawaii",
    source: "YouTube",
    sourceHref: "https://www.youtube.com/watch?v=FMguu_KVjIs",
    sourceIcon: "youtube" as const,
    copy:
      "Anthony Maisonet and Craig Opie joined PACE Hawaii's The Next Wave to discuss cybersecurity, startups, leadership under pressure, and building systems for regulated environments.",
    tags: ["Founders", "Startups", "Security truth"]
  },
  {
    title: "Holocron Security x ISC2 Hawaii",
    date: "2026",
    location: "Hawaii cybersecurity community",
    source: "YouTube Short",
    sourceHref: "https://www.youtube.com/shorts/lf5jXR_SdbE",
    sourceIcon: "youtube" as const,
    copy:
      "A community-focused short highlighting Holocron's participation in Hawaii's cybersecurity ecosystem and the relationships behind Conscious Security.",
    tags: ["ISC2 Hawaii", "Cybersecurity", "Community"]
  }
];

export function NewsIndexPage() {
  const [linkedInFeed, setLinkedInFeed] = useState<LinkedInFeed>(fallbackLinkedInFeed);

  useEffect(() => {
    let isMounted = true;

    fetch("/assets/data/linkedin-updates.json", { cache: "no-store" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load LinkedIn updates: ${response.status}`);
        }
        return response.json() as Promise<LinkedInFeed>;
      })
      .then((feed) => {
        if (isMounted) {
          setLinkedInFeed(feed);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLinkedInFeed(fallbackLinkedInFeed);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useDocumentMeta({
    canonicalPath: "/news",
    description:
      "Holocron Security insights, media, articles, and events on Conscious Security, secure AI, CMMC operations, and machine-speed defense.",
    title: "Insights | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: contactHref, label: "Talk to Holocron", variant: "primary" },
          { href: "/solutions", label: "Explore Solutions", variant: "secondary" }
        ]}
        copy="Follow Holocron's public perspective on Conscious Security, secure AI implementation, CMMC operations, and machine-speed defense."
        eyebrow="Insights"
        title="Signals for regulated security operations."
      />

      <section className="surface-dark linkedin-feed-section">
        <div className="section-shell">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">LinkedIn updates</p>
              <h2 className="section-title">What Holocron is sharing now.</h2>
              <p className="section-copy">
                Updates from the Holocron Security LinkedIn page, synced into the Insights experience for visitors who
                want the latest public activity without leaving the site.
              </p>
            </div>
            <SmartLink className="button secondary" href={linkedInFeed.source.profileUrl || linkedInPageHref}>
              View LinkedIn Page
            </SmartLink>
          </div>

          <div className="linkedin-feed-layout">
            <aside className="linkedin-source-panel">
              <div className="icon-badge icon-badge--glass">
                <Icon name="linkedin" />
              </div>
              <p>{linkedInFeed.source.platform}</p>
              <h3>{linkedInFeed.source.profileName}</h3>
              <span>
                {linkedInFeed.source.syncedAt
                  ? `Last synced ${new Date(linkedInFeed.source.syncedAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}`
                  : "Sync pending"}
              </span>
            </aside>

            {linkedInFeed.updates.length > 0 ? (
              <div className="linkedin-update-grid">
                {linkedInFeed.updates.slice(0, 6).map((update) => (
                  <article key={update.id} className="linkedin-update-card">
                    <div className="linkedin-update-meta">
                      <span>{update.publishedLabel}</span>
                      <span>LinkedIn</span>
                    </div>
                    <h3>{update.title}</h3>
                    <p>{update.summary}</p>
                    <div className="linkedin-tag-row">
                      {update.tags.slice(0, 3).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                    <SmartLink className="inline-link" href={update.url}>
                      Open on LinkedIn
                    </SmartLink>
                  </article>
                ))}
              </div>
            ) : (
              <div className="linkedin-empty-state">
                <h3>LinkedIn sync is ready.</h3>
                <p>
                  Add the LinkedIn organization URN and access token to the sync job to publish live page updates into
                  this section.
                </p>
                <SmartLink className="inline-link" href={linkedInPageHref}>
                  Open Holocron on LinkedIn
                </SmartLink>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="surface-light">
        <div className="section-shell">
          <div className="section-heading-row section-heading-row--light">
            <div>
              <p className="section-kicker section-kicker--light">Published work</p>
              <h2 className="section-title section-title--light">Articles, journals, and credibility signals.</h2>
            </div>
            <SmartLink className="button button-outline-violet" href="mailto:info@holocronsecurity.com?subject=Media%20Inquiry">
              Media Inquiry
            </SmartLink>
          </div>
          <div className="publication-grid">
            {publishedPlaceholders.map((item) => (
              <article key={item.title} className="publication-card">
                <p>{item.type}</p>
                <h3>{item.title}</h3>
                <span>{item.source}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark">
        <div className="section-shell">
          <div className="section-intro">
            <p className="section-kicker">Featured themes</p>
            <h2 className="section-title">The topics our market is asking about.</h2>
          </div>
          <div className="use-case-grid">
            {topicCards.map((card) => (
              <article key={card.title} className="use-case-card">
                <div className="icon-badge icon-badge--glass">
                  <Icon name={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-light">
        <div className="section-shell section-shell--narrow">
          <p className="section-kicker section-kicker--light">Media</p>
          <h2 className="section-title section-title--light">Podcast and industry coverage.</h2>
          <div className="content-panel content-panel--light content-panel--editorial">
            <p>
              Holocron participates in public conversations about machine-speed cyber defense, autonomous security
              operations, quantum-era risk, and the limits of human-in-the-loop response models.
            </p>
            <p>
              For speaking requests, media inquiries, or background on Holocron&apos;s Conscious Security category,
              contact the Holocron team directly.
            </p>
          </div>

          <div className="youtube-media-block">
            <div className="youtube-feature-panel">
              <div className="youtube-feature-frame">
                <iframe
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  src={`https://www.youtube-nocookie.com/embed/${youtubeVideos[0].id}`}
                  title={youtubeVideos[0].title}
                />
              </div>
              <div className="youtube-feature-copy">
                <div className="icon-badge icon-badge--glass icon-badge--youtube">
                  <Icon name="youtube" />
                </div>
                <p className="section-kicker section-kicker--light">Holocron YouTube</p>
                <h3>{youtubeVideos[0].title}</h3>
                <p>{youtubeVideos[0].summary}</p>
                <SmartLink
                  className="inline-link"
                  href={`https://www.youtube.com/watch?v=${youtubeVideos[0].id}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Watch on YouTube
                </SmartLink>
              </div>
            </div>

            <div className="youtube-video-grid">
              {youtubeVideos.slice(1).map((video) => (
                <article key={video.id} className="youtube-video-card">
                  <SmartLink
                    className="youtube-thumbnail-link"
                    href={`https://www.youtube.com/watch?v=${video.id}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <img
                      alt=""
                      loading="lazy"
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                    />
                    <span>
                      <Icon name="youtube" />
                    </span>
                  </SmartLink>
                  <div className="youtube-video-copy">
                    <p>{video.label}</p>
                    <h3>{video.title}</h3>
                    <span>{video.summary}</span>
                    <SmartLink
                      className="inline-link"
                      href={`https://www.youtube.com/watch?v=${video.id}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Watch video
                    </SmartLink>
                  </div>
                </article>
              ))}
            </div>

            <SmartLink className="button button-outline-violet youtube-channel-button" href={youtubeChannelHref}>
              View YouTube Channel
            </SmartLink>
          </div>

          <div className="section-divider" />

          <p className="section-kicker section-kicker--light">Recent participation</p>
          <h2 className="section-title section-title--light">Small signals from where Holocron shows up.</h2>
          <div className="event-list">
            {participationSnippets.map((event) => (
              <article key={`${event.title}-${event.date}`} className="event-row-card event-row-card--full">
                <div className="event-copy">
                  <div className="event-source-row">
                    <span className="event-source-pill">
                      <Icon name={event.sourceIcon} />
                      {event.source}
                    </span>
                    <span>{event.date}</span>
                  </div>
                  <h3>{event.title}</h3>
                  <div className="event-meta">
                    <span>
                      <Icon className="meta-icon" name="location" />
                      {event.location}
                    </span>
                  </div>
                  <p>{event.copy}</p>
                  <div className="event-tag-row">
                    {event.tags.map((tag) => (
                      <span key={`${event.title}-${tag}`}>{tag}</span>
                    ))}
                  </div>
                  <SmartLink className="inline-link" href={event.sourceHref} rel="noreferrer" target="_blank">
                    View source
                  </SmartLink>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
